// @mui
import { Card, Stack, CardContent, Typography, useMediaQuery, Box } from "@mui/material";

import dayjs from "dayjs";
// utils
import { fCurrency } from "../../../../fe-ui/utils/formatNumber";

// components

import { PropertyPromotionLabel } from "../PropertyPromotionLabel";

import { IPropertyListItem } from "../../../../@types/property";
import { useMemo, useState } from "react";
import { useTheme } from "@mui/system";
import { PropertyCardSkeleton } from "./PropertyCardSkeleton";

import { Link } from "../../../../fe-ui/atoms/Link/Link";
import { useTranslation } from "../../../../fe-ui/hooks/use-translation";
import { S3ImageLight } from "../../../../fe-ui/atoms/S3Image/S3ImageLight";
import { CardCarousel } from "../../../../fe-ui/blocks/CardCarousel";
import { fDate } from "../../../../fe-ui/utils/formatTime";
import { truncate } from "../../../../fe-ui/utils/truncate";
import { useGetUnitGroupPromotionLabels } from "../../../../api/hooks";
import { useAttributeMapping } from "../../../../hooks/useAttributeMapping";

// ----------------------------------------------------------------------

// TODO: Fix types
type Props = IPropertyListItem & {
  subtitle?: string;
} & {
  minStayDays?: number;
  showTotalPrice?: boolean;
  openInNewTab?: boolean;
};

const IMAGE_HEIGHT_MOBILE = 300;
const IMAGE_HEIGHT_DEFAULT = 240;
const CARD_MAXWIDTH_MOBILE = 600;
const CARD_MAXWIDTH_DEFAULT = 420;

function PropertyCardComponent(props: Props) {
  const {
    area,
    id,
    title,
    description,
    image,
    imageList,
    pricePerNightDetails,
    link,
    arrivalDate,
    departureDate,
    subtitle,
    hotelId,
    minStayDays,
    priceDetails,
    showTotalPrice,
    openInNewTab,
  } = props;

  const priceWithTax = pricePerNightDetails?.priceWithTax;
  const priceWithTaxBeforeDiscount = pricePerNightDetails?.priceWithTaxBeforeDiscount;

  const t = useTranslation();

  const { data: allPromotionLabels = [] } = useGetUnitGroupPromotionLabels();
  const { unitAttributes } = useAttributeMapping({ unitAttributes: props.attributes || [] });
  const attributesToShow = unitAttributes
    .flatMap((attr) => attr.attributes)
    .filter((attr) => attr.show_in_attributes_on_mini_cards);

  const promotionLabels = useMemo(() => {
    return [
      ...(props.promotionLabels?.map((lbl) => allPromotionLabels.find((h) => h.id === lbl.id)) || []),
      minStayDays &&
        minStayDays > 1 && {
          id: 1000,
          title: t("min-days-stay", {
            count: minStayDays,
          }),
          backgroundColor: "#777",
        },
    ].filter(Boolean);
  }, [allPromotionLabels, minStayDays, props.promotionLabels, t]);

  const [isRaised, setRaised] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down("sm"));
  const imageHeight = isXS ? IMAGE_HEIGHT_MOBILE : IMAGE_HEIGHT_DEFAULT;
  const cardMaxWidth = isXS ? CARD_MAXWIDTH_MOBILE : CARD_MAXWIDTH_DEFAULT;
  const truncatedDescription = truncate(description || "", 65);

  const datesForOffer =
    arrivalDate && departureDate && `${fDate(arrivalDate, "DD MMM")}–${fDate(departureDate, "DD MMM")}`;
  const nightsAmount = dayjs(departureDate).diff(arrivalDate, "day");

  const handleAfterChange = (index: number) => {
    setSlideIndex(index);
  };

  const renderImage = (image: string, title: string) => {
    return (
      <Box style={{ position: "relative", overflow: "hidden", height: imageHeight }}>
        <S3ImageLight src={image} alt={`${title}`} size="small" />
      </Box>
    );
  };

  // We have to lowercase the link for SEO purposes but keep original query params as is
  const lowercaseLink = useMemo(() => {
    if (!link) return "";
    const [base, queryParams] = link.split("?");
    return queryParams ? `${base.toLowerCase()}?${queryParams}` : base.toLowerCase();
  }, [link]);

  return (
    <Link
      key={id}
      data-ym={"UNIT_CARD"}
      data-testid={`property-card-${id}`}
      sx={{ position: "relative" }}
      href={lowercaseLink}
      target={openInNewTab !== false ? "_blank" : undefined}
      underline={"none"}
    >
      <Card
        onMouseEnter={() => setRaised(true)}
        onMouseLeave={() => setRaised(false)}
        onTouchStart={() => setRaised(true)}
        elevation={isRaised ? 4 : 2}
        sx={{
          transition: "all .1s ease-in",
          transform: isRaised ? "translateY(-1%)" : undefined,
          maxWidth: cardMaxWidth,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {imageList && imageList.length > 1 && isRaised ? (
          <CardCarousel
            height={imageHeight}
            initialSlide={slideIndex}
            isHovered={isRaised}
            afterChange={handleAfterChange}
          >
            {imageList.map((img) => (
              <div key={img}>{renderImage(img, title)}</div>
            ))}
          </CardCarousel>
        ) : imageList && imageList?.length > 1 ? (
          renderImage(imageList[slideIndex], title)
        ) : (
          renderImage(image, title)
        )}
        <CardContent sx={{ padding: 1 }}>
          {subtitle && (
            <Typography
              noWrap
              variant={"body2"}
              component={hotelId ? Link : "span"}
              href={hotelId ? `/hotel/${hotelId}` : undefined}
              data-ym={`CARD_PROPERTY_LINK`}
              sx={{
                display: "block",
                fontWeight: "fontWeightMedium",
              }}
            >
              {subtitle || " "}
            </Typography>
          )}
          <Typography noWrap variant={"subtitle1"} sx={{ marginBottom: 0.5 }}>
            {title}
          </Typography>

          {attributesToShow.length > 0 ? (
            <Typography variant={"body2"} component={"p"}>
              {area && area > 0
                ? t.rich("square_meters", {
                    meters: area,
                    sup: (x) => (
                      <>
                        <sup>{x}</sup>
                        {" • "}
                      </>
                    ),
                  })
                : null}
              {attributesToShow.map((a) => a.title).join(" • ")}
            </Typography>
          ) : (
            <Typography
              variant={"body2"}
              component={"p"}
              dangerouslySetInnerHTML={{
                __html: truncatedDescription,
              }}
              sx={{
                display: "-webkit-box",
                maxWidth: "100%",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical" as const,
                overflow: "hidden",
              }}
            />
          )}
        </CardContent>
        <PropertyPromotionLabel items={promotionLabels} />
        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{ padding: 1, mt: "auto" }}>
          {datesForOffer ? <Typography variant="body2">{datesForOffer}</Typography> : <div />}
          {pricePerNightDetails &&
            (priceWithTaxBeforeDiscount || priceWithTax) &&
            (showTotalPrice && priceDetails ? (
              <>
                <Typography variant="body2">
                  {priceDetails.priceWithTaxBeforeDiscount &&
                    priceDetails.priceWithTaxBeforeDiscount !== priceDetails.priceWithTax && (
                      <Typography
                        component={"span"}
                        variant={"body2"}
                        sx={{
                          color: "text.disabled",
                          textDecoration: "line-through",
                        }}
                      >
                        {fCurrency(Math.round(priceDetails.priceWithTaxBeforeDiscount))}
                      </Typography>
                    )}
                  &nbsp;
                  {priceWithTax && <b>{fCurrency(Math.ceil(priceDetails.priceWithTax))}</b>}
                  {nightsAmount && (
                    <>
                      &nbsp;
                      {t("Property_forNights", {
                        count: nightsAmount,
                      })}
                    </>
                  )}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant={"body2"}>
                  {priceWithTaxBeforeDiscount && priceWithTaxBeforeDiscount !== priceWithTax
                    ? t.rich("property_card_price_discount", {
                        discount: () => (
                          <Typography
                            component={"span"}
                            variant={"body2"}
                            sx={{
                              color: "text.disabled",
                              textDecoration: "line-through",
                            }}
                          >
                            {fCurrency(Math.round(priceWithTaxBeforeDiscount))}
                          </Typography>
                        ),
                        price: () => priceWithTax && <b>{fCurrency(Math.ceil(priceWithTax))}</b>,
                      })
                    : t.rich("property_card_price", {
                        price: () => priceWithTax && <b>{fCurrency(Math.ceil(priceWithTax))}</b>,
                      })}
                </Typography>
              </>
            ))}
        </Stack>
      </Card>
    </Link>
  );
}

export function PropertyCard(
  p: Props & {
    isLoading?: boolean;
    openInNewTab?: boolean;
  }
) {
  // TODO: Скелетоны в отдельные компоненты везде
  if (p.isLoading) {
    return <PropertyCardSkeleton />;
  }
  return <PropertyCardComponent {...p} />;
}
